import { useState, useEffect, useCallback, forwardRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_KEY = "cookie-consent";
const GPC_ACK_KEY = "cookie-consent-gpc-acknowledged";

function getStoredPreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(COOKIE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function storePreferences(prefs: CookiePreferences) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify(prefs));
  window.dispatchEvent(new Event("cookie-consent-updated"));
}

function detectGPC(): boolean {
  try {
    // Firefox/Brave/DuckDuckGo/etc. expose navigator.globalPrivacyControl
    // when the user has enabled the GPC signal.
    return (
      typeof navigator !== "undefined" &&
      (navigator as unknown as { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true
    );
  } catch {
    return false;
  }
}

export const CookieConsent = forwardRef<HTMLDivElement>(function CookieConsent(_, ref) {
  const [visible, setVisible] = useState(false);
  // Show granular category toggles by default so consent is informed at first
  // interaction (ICO/EDPB guidance — no dark patterns).
  const [showDetails, setShowDetails] = useState(true);
  const [gpcActive, setGpcActive] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const openBanner = useCallback(() => {
    const stored = getStoredPreferences();
    if (stored) setPreferences(stored);
    setShowDetails(true);
    setGpcActive(detectGPC());
    setVisible(true);
  }, []);

  useEffect(() => {
    const stored = getStoredPreferences();
    const gpc = detectGPC();
    setGpcActive(gpc);

    if (!stored) {
      // Honour Global Privacy Control automatically: treat it as a
      // legally-binding "reject non-essential" signal and suppress the banner
      // so we don't nag a user who has already expressed their choice.
      if (gpc) {
        const prefs = { necessary: true, analytics: false, marketing: false };
        storePreferences(prefs);
        try {
          localStorage.setItem(GPC_ACK_KEY, new Date().toISOString());
        } catch {
          /* ignore quota */
        }
        return;
      }
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for custom event to reopen
  useEffect(() => {
    const handler = () => openBanner();
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, [openBanner]);

  const handleAcceptAll = () => {
    storePreferences({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const handleRejectAll = () => {
    storePreferences({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const handleSavePreferences = () => {
    storePreferences(preferences);
    setVisible(false);
  };

  // Mark body so other floating UI (WhatsApp, scroll-to-top) can hide on mobile
  useEffect(() => {
    if (visible) {
      document.body.dataset.cookieBanner = "visible";
    } else {
      delete document.body.dataset.cookieBanner;
    }
    return () => {
      delete document.body.dataset.cookieBanner;
    };
  }, [visible]);

  if (!visible) return null;

  const categories = [
    {
      key: "necessary" as const,
      label: "Strictly Necessary",
      description: "Essential for the website to function. These cannot be disabled.",
      locked: true,
    },
    {
      key: "analytics" as const,
      label: "Analytics",
      description: "Google Analytics 4 — helps us understand aggregate site usage to improve the experience.",
      locked: false,
    },
    {
      key: "marketing" as const,
      label: "Marketing",
      description: "Currently unused. Reserved for any future advertising or remarketing pixels.",
      locked: false,
    },
  ];

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-0 bottom-0 z-40 p-4 md:p-6 animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="mx-auto max-w-2xl rounded-2xl border bg-card shadow-2xl">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Cookie className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 id="cookie-consent-title" className="font-display text-base font-semibold">
                We value your privacy
              </h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                We use cookies to enhance your experience and analyse site traffic. You can accept, reject, or choose which categories to allow. See our{" "}
                <Link to="/privacy-policy" className="text-primary underline underline-offset-2 hover:no-underline">
                  Privacy Policy
                </Link>{" "}
                for details.
              </p>
            </div>
          </div>

          {gpcActive && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-foreground">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                <strong>Global Privacy Control detected.</strong> Non-essential cookies are disabled by default in line with your browser signal. You can override this below if you wish.
              </span>
            </div>
          )}

          <button
            onClick={() => setShowDetails(!showDetails)}
            className="mt-4 flex items-center gap-1 text-xs font-semibold text-foreground/80 hover:underline hover:text-foreground"
            aria-expanded={showDetails}
          >
            {showDetails ? "Hide" : "Show"} cookie categories
            {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {showDetails && (
            <div className="mt-4 space-y-3 border-t pt-4">
              {categories.map((cat) => (
                <div key={cat.key} className="flex items-center justify-between gap-4 rounded-lg bg-muted/50 p-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{cat.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>
                  </div>
                  <Switch
                    checked={preferences[cat.key]}
                    onCheckedChange={(checked) =>
                      !cat.locked && setPreferences((prev) => ({ ...prev, [cat.key]: checked }))
                    }
                    disabled={cat.locked}
                    className="shrink-0"
                    aria-label={`Toggle ${cat.label} cookies`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Equal-weight Accept / Reject controls (ICO/CNIL/EDPB compliant) */}
          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button
              size="sm"
              variant="outline"
              onClick={handleRejectAll}
              className="w-full border-foreground/20 font-semibold"
            >
              Reject All
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSavePreferences}
              className="w-full border-foreground/20 font-semibold"
            >
              Save Preferences
            </Button>
            <Button
              size="sm"
              onClick={handleAcceptAll}
              className="w-full gradient-orange border-0 font-semibold hover-glow"
            >
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});
CookieConsent.displayName = "CookieConsent";
